import { useCallback, useMemo, useState } from 'react'
import { match, matchPath, useHistory, useLocation } from 'react-router-dom'
import { CIMSModule, CIMSPage, pathify } from '@cims-online/shared'

export interface RouteMatches {
  projectId?: string
  module?: string
  workflowId?: string
  page?: string
  home?: 'home'
  deepLink?: string
}

export function useRouteMatches(pathname?: string) {
  const { push } = useHistory()
  const location = useLocation()
  const [match, setMatch] = useState<match<RouteMatches> | null>(null)
  // matchPath wants to update on every render so we're wrapping it in useMemo
  useMemo(() => {
    setMatch(
      matchPath<RouteMatches>(pathname ?? location.pathname, [
        pathify(
          CIMSPage.projectId,
          'workflows',
          ':workflowId',
          ':page',
          CIMSPage.deepLink
        ),
        pathify(CIMSPage.projectId, 'workflows', ':workflowId', ':page'),
        pathify(CIMSPage.projectId, ':module', ':page', CIMSPage.deepLink),
        pathify(CIMSPage.projectId, ':module', ':page'),
        pathify(CIMSPage.projectId, CIMSModule.Home.pathname),
        pathify(':home'),
      ])
    )
  }, [pathname, location.pathname])

  const replaceHistory = useCallback(
    (matches: ReplaceMatch | ReplaceMatch[]) => {
      const { path, url } = match ?? {}
      const pathSegments = path?.split('/')?.slice(1) ?? []
      const urlSegments = url?.split('/')?.slice(1) ?? []
      const resolved = Array.isArray(matches) ? matches : [matches]
      const segments = pathSegments.reduce((segments, segment, i) => {
        const found = resolved.find(({ key }) => `:${key}` === segment)
        if (!found) segments.push(urlSegments[i])
        else if (found.action === 'replace') segments.push(found.value)
        else if (found.action === 'remove') return segments
        return segments
      }, Array<string>())
      return push(pathify(...segments))
    },
    [push, match]
  )
  const param = useCallback(
    (key?: keyof RouteMatches) => {
      if (!match?.params) return undefined
      if (!key) return undefined
      return match.params?.[key]
    },
    [match?.params]
  )

  return {
    ...(match ?? {}),
    /**
     * a callback to update, and navigate, to the url based on the route match
     * information
     *
     * @param matches {@link ReplaceMatch}
     */
    replaceHistory,
    /**
     * gets the value associated with one of the params
     *
     * @param key a {@link RouteMatches} property to get
     */
    param,
  } as const
}

interface Key {
  /**
   * which part of the url to match
   *
   * if no key can be found, it is ignored
   */
  key: keyof RouteMatches
}

interface ReplaceMatchValue {
  /**
   * replace the value with the supplied {@link value}
   */
  action: 'replace'
  /**
   * the new value
   */
  value: string
}

interface RemoveMatchValue {
  /**
   * remove the value from the url
   */
  action: 'remove'
}

type ReplaceMatch = Key & (ReplaceMatchValue | RemoveMatchValue)
